import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";
import { getColor } from "../../brandConfig";

// Import email components (lazy-loaded for performance)
const EmailsList = React.lazy(() => import("./EmailsList"));
const ComposeRedirect = React.lazy(() => import("./ComposeRedirect"));
const EmailRedirect = React.lazy(() => import("./EmailRedirect"));
const InboundEmailRedirect = React.lazy(() => import("./InboundEmailRedirect"));
const AddressBook = React.lazy(() => import("./AddressBook").then(module => ({ default: module.AddressBook })));
const EmailAccountsAdmin = React.lazy(() => import("./EmailAccountsAdmin").then(module => ({ default: module.EmailAccountsAdmin })));
const ImprovMXAdmin = React.lazy(() => import("./ImprovMXAdmin").then(module => ({ default: module.ImprovMXAdmin })));
const Microsoft365Admin = React.lazy(() => import("./Microsoft365Admin").then(module => ({ default: module.Microsoft365Admin })));
const EmailAiMemoriesList = React.lazy(() => import("./EmailAiMemoriesList"));
const EmailAiRulesList = React.lazy(() => import("./EmailAiRulesList"));
const EmailAiTrainer = React.lazy(() => import("./EmailAiTrainer"));
const EmailBacklogPage = React.lazy(() => import("./EmailBacklogPage"));
const EmailSummariesList = React.lazy(() => import("./EmailSummariesList"));
const EmailSummaryDetail = React.lazy(() => import("./EmailSummaryDetail"));
const EmailAiPreferences = React.lazy(() => import("./EmailAiPreferences"));
const EmailDashboard = React.lazy(() => import("./EmailDashboard"));
const EmailOutstandingQuestionsPage = React.lazy(() => import("./EmailOutstandingQuestionsPage"));

/**
 * Emails Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (emails/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "admin", "addressBook")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/emails/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 */
const emailsModuleConfig: ModuleConfig = {
    // MODULE IDENTITY
    id: "emails",
    name: "Email Marketing",
    description: "Compose and send branded HTML emails",
    version: "1.0.2",
    enabled: true,
    category: "communication",
    order: 10,
    icon: "✉️",
    color: getColor("primaryBlue"),

    // ROUTES - permissionLevel is resolved from backend at runtime
    routes: [
        {
            path: "/emails",
            component: EmailsList,
            permissionLevel: "view",
            useLayout: true,
            // fillViewport pins the frame to exactly 100dvh (mobile-safe — no
            // 100vh guesswork behind the iOS address bar) and the shell owns all
            // internal scrolling; the footer would sit below a non-scrolling
            // frame, so it's off. Gmail-style app page, not a document page.
            layoutOptions: { noContainer: true, fillViewport: true, noFooter: true },
        },
        {
            // /emails/new retired — composing is inline in the Gmail UI.
            // This route only forwards old links (preserving ?action/?to/?draftId).
            path: "/emails/new",
            component: ComposeRedirect,
            permissionLevel: "create",
            useLayout: true,
        },
        {
            path: "/emails/address-book",
            component: AddressBook,
            permissionLevel: "addressBook",
            useLayout: true,
        },
        {
            path: "/emails/admin/accounts",
            component: EmailAccountsAdmin,
            permissionLevel: "admin",
            useLayout: true,
        },
        {
            path: "/emails/admin/improvmx",
            component: ImprovMXAdmin,
            permissionLevel: "admin",
            useLayout: true,
        },
        {
            path: "/emails/admin/microsoft365",
            component: Microsoft365Admin,
            permissionLevel: "admin",
            useLayout: true,
        },
        {
            path: "/email/:id",
            // /email/:id retired as a page — redirects into the Gmail UI
            component: EmailRedirect,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            // /inbox/:id retired as a page (2026-06-19) — inbound email is read inside the
            // Gmail UI (/emails). This route now just redirects old/external deep links there.
            path: "/inbox/:id",
            component: InboundEmailRedirect,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/emails/ai/memories",
            component: EmailAiMemoriesList,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/emails/ai/rules",
            component: EmailAiRulesList,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/emails/ai",
            component: EmailAiTrainer,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/emails/ai/backlog",
            component: EmailBacklogPage,
            permissionLevel: "edit",
            useLayout: true,
        },
        {
            path: "/emails/ai/summaries",
            component: EmailSummariesList,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/emails/summaries/:id",
            component: EmailSummaryDetail,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/emails/ai/preferences",
            component: EmailAiPreferences,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/emails/dashboard",
            component: EmailDashboard,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/emails/ai/questions",
            component: EmailOutstandingQuestionsPage,
            permissionLevel: "edit",
            useLayout: true,
        }
    ],

    // NAVIGATION - permissionLevel is resolved from backend at runtime
    navigation: [
        {
            label: "All Emails",
            path: "/emails",
            icon: "📧",
            permissionLevel: "view",
        },
        {
            label: "Address Book",
            path: "/emails/address-book",
            icon: "📖",
            permissionLevel: "addressBook",
        },
        {
            label: "Compose Email",
            path: "/emails/new",
            icon: "✍️",
            permissionLevel: "create",
        },
        {
            label: "AI Memories",
            path: "/emails/ai/memories",
            icon: "🧠",
            permissionLevel: "view",
        },
        {
            label: "AI Rules",
            path: "/emails/ai/rules",
            icon: "⚡️",
            permissionLevel: "view",
        },
        {
            label: "AI Trainer",
            path: "/emails/ai",
            icon: "🎓",
            permissionLevel: "view",
        },
        {
            label: "Backlog Processor",
            path: "/emails/ai/backlog",
            icon: "🗂",
            permissionLevel: "edit",
        },
        {
            label: "Inbox Summaries",
            path: "/emails/ai/summaries",
            icon: "📅",
            permissionLevel: "view",
        },
        {
            label: "Summary Preferences",
            path: "/emails/ai/preferences",
            icon: "⚙️",
            permissionLevel: "view",
        },
        {
            label: "Email Dashboard",
            path: "/emails/dashboard",
            icon: "📊",
            permissionLevel: "view",
        },
        {
            label: "Outstanding Questions",
            path: "/emails/ai/questions",
            icon: "❓",
            permissionLevel: "edit",
        },
        {
            label: "Email Accounts Admin",
            path: "/emails/admin/accounts",
            icon: "🔧",
            permissionLevel: "admin",
        },
        {
            label: "ImprovMX Aliases",
            path: "/emails/admin/improvmx",
            icon: "📨",
            permissionLevel: "admin",
        },
        {
            label: "Microsoft 365",
            path: "/emails/admin/microsoft365",
            icon: "📧",
            permissionLevel: "admin",
        }
    ],

    // Permissions come from the backend (single source of truth).
    // See: business-builder-backend/src/modules/emails/permissions.ts
    // These empty arrays get replaced at runtime by ModulePermissionsContext.
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
        send: [],
        admin: [],
        addressBook: [],
    },

    // QUICK ACTIONS
    quickActions: [
        {
            label: "Compose Email",
            path: "/emails/new",
            icon: "✍️",
            description: "Create a new email"
        }
    ],

    // 📝 CHANGELOG
    changelog: [
        {
            version: "1.0.1",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "✅ Production-ready email marketing system",
                "✅ Complete inbox and outbox management",
                "✅ Advanced address book with contact management",
                "✅ Multi-account email administration",
                "✅ Communication task tracking",
                "✅ Attachment support for all email types",
                "✅ Project integration for email-to-task conversion"
            ],
            notes: "Official stable release with comprehensive email communication features."
        },
        {
            version: "0.8.0",
            releaseDate: "2025-09-01",
            title: "Refactoring & Tagging",
            features: [
                "🔨 Removed deprecated EmailInbox component",
                "✨ Enhanced EmailsList with tagging features (450 insertions)",
                "✨ Improved InboundEmailDetail with tags (258 insertions)",
                "✨ Better email organization and filtering",
                "🔨 Code cleanup and optimization"
            ],
            notes: "Major refactoring improving email organization with tagging system."
        },
        {
            version: "0.7.0",
            releaseDate: "2024-08-31",
            title: "UI Enhancements & Polish",
            features: [
                "✨ Enhanced AddressBook UI (116 lines)",
                "✨ Improved EmailsList interface (103 lines)",
                "✨ Better InboundEmailDetail presentation (71 lines)",
                "✨ Refined email composition UI (60 lines)",
                "✨ Improved responsive design"
            ],
            notes: "UI refinements for better user experience across all email components."
        },
        {
            version: "0.6.0",
            releaseDate: "2024-08-30",
            title: "Reply Functionality & Email Composer",
            features: [
                "✨ Added reply functionality to InboundEmailDetail (33 lines)",
                "✨ Enhanced NewEmail with reply support (44 lines)",
                "✨ Created email composer component",
                "✨ Improved email threading",
                "🔨 Refactored EmailView and NewEmail (438 deletions)"
            ],
            notes: "Reply and threading capabilities for better email communication."
        },
        {
            version: "0.5.0",
            releaseDate: "2024-08-29",
            title: "Project Integration & Performance",
            features: [
                "✨ Added ConvertToProject functionality (727 lines)",
                "✨ Enhanced EmailView with project integration (32 lines)",
                "✨ Improved InboundEmailDetail workflow (59 lines)",
                "✨ Refactored EmailsList for better query performance (78 lines)",
                "✨ Added client email integration (154 lines)"
            ],
            notes: "Deep integration with project management and performance optimizations."
        },
        {
            version: "0.4.0",
            releaseDate: "2024-08-28",
            title: "UI Responsiveness Update",
            features: [
                "✨ Enhanced AddressBook responsiveness (81 lines)",
                "✨ Updated EmailAccountsAdmin UI (228 lines)",
                "✨ Improved EmailInbox consistency (272 lines)",
                "✨ Better mobile experience across all components"
            ],
            notes: "Comprehensive responsiveness improvements for mobile and tablet devices."
        },
        {
            version: "0.3.0",
            releaseDate: "2024-08-26",
            title: "Address Book & Account Management",
            features: [
                "✨ Enhanced EmailAccountsAdmin (177 lines)",
                "✨ Advanced NewEmail features (394 insertions)",
                "✨ Improved EmailView with attachments (285 insertions)",
                "✨ Address book functionality (326 lines)",
                "✨ Better email account configuration"
            ],
            notes: "Address book and multi-account management features."
        },
        {
            version: "0.2.0",
            releaseDate: "2023-08-25",
            title: "Feature Expansion - Inbound & Attachments",
            features: [
                "✨ Added AddressBook component (670 lines)",
                "✨ Created EmailInbox component (544 lines)",
                "✨ Implemented EmailAccountsAdmin (508 lines)",
                "✨ Enhanced with attachment support (227 insertions)",
                "✨ Added search and filter functionality (1,405 insertions)",
                "✨ Created UnifiedEmailCard component (165 lines)",
                "✨ Added CommunicationTaskList (417 lines)",
                "✨ Implemented CommunicationTaskModal (458 lines)",
                "✨ Created InboundEmailDetail component (368 lines)",
                "✨ Enhanced brand configuration integration"
            ],
            notes: "Massive feature expansion with inbound email, attachments, and task management."
        },
        {
            version: "0.1.0",
            releaseDate: "2023-08-24",
            title: "Initial Email Management System",
            features: [
                "✅ Created EmailView component (473 lines)",
                "✅ Created EmailsList component (397 lines)",
                "✅ Created NewEmail component (388 lines)",
                "✅ Basic email composition and sending",
                "✅ Markdown editor integration (remark-gfm)",
                "✅ Initial GraphQL integration",
                "✅ Draft and sent email management",
                "✅ Module configuration and routing"
            ],
            notes: "Initial implementation of the email marketing system with 1,334 lines of code."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: []
};

export default emailsModuleConfig;